// src/pages/partners/PartnerPriceUpload.tsx
//
// PartnerPriceUpload
// - Admin page to upload partner product pricing (bulk) and review upload histories.
// - Table UI is standardized with:
//   - StandardTableToolbar + StandardTableFrame + StandardPagination
// - Navigation policy:
//   - Index (#) is clickable and navigates to the upload detail page.
// - Data policy:
//   - Deleted histories (isDeleted === true) are excluded from rendering.
// - Visual policy:
//   - Rows with failedCount > 0 are highlighted as "warning" for quick scanning.
//   - Upload status is rendered via a standardized Chip.
// - Interaction policy:
//   - Delete is icon-only and uses shared Confirm + SystemAlert (no window.confirm/alert).
//   - Upload success triggers list + count refetch, then navigates to the detail page.
//
// Implementation notes
// - This module intentionally provides a default export only.
//   Route imports must use: `import PartnerPriceUpload from ...`
// - React Hooks must not be called conditionally.
//   Therefore, the component does not early-return even when partnerId is invalid.
//   Downstream query hooks are expected to guard invalid ids via `enabled`.

import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, IconButton, TableCell, TableRow, Typography } from '@mui/material';
import { DeleteOutline, Download } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';

import DragDropFile from '@components/common/DragDropFile';

import { timestampToDate } from '@utils/time';
import { downloadFile } from '@utils/file';
import { PARTNER_PRICE_UPLOAD_TEMPLATE } from '@constants/templates';
import { getBulkRunStatusMeta } from '@constants/bulkLabels';

import { usePriceUploadHistories } from '@queries/partners/usePriceUploadHistories';
import { usePriceUploadHistoriesCount } from '@queries/partners/usePriceUploadHistoriesCount';
import { deletePriceUploadHistory, uploadPriceFile } from '@apis/partnerBulk';

import { usePageHeader } from '@components/common/pageHeader.context';
import { useConfirm } from '@components/common/confirm/confirm.context';
import { useSystemAlert } from '@components/common/systemAlert/systemAlert.context';

import { StandardPagination, StandardTableFrame, StandardTableToolbar, cellSx } from '@components/common/table';

import type { PartnerProductPriceBulkUpload } from '../../models/partnerBulk';

const PAGE_SIZE = 50;

/* ------------------------------------------------------------------ */
/* Formatting helpers                                                  */
/* ------------------------------------------------------------------ */

const formatNumber = (n: unknown) => {
  const v = typeof n === 'number' ? n : Number(n);
  if (!Number.isFinite(v)) return '-';
  return v.toLocaleString();
};

const formatTimestamp = (ts: unknown) => {
  const v = typeof ts === 'number' ? ts : Number(ts);
  if (!Number.isFinite(v) || v <= 0) return '-';
  return timestampToDate(v);
};

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

const PartnerPriceUpload = (): React.ReactElement | null => {
  const [pageNo, setPageNo] = useState(1);

  const navigate = useNavigate();
  const { partnerId: partnerIdParam } = useParams();

  /**
   * Partner identifier
   * - Keep a safe numeric id without early-returning before hooks.
   * - Query hooks should guard invalid ids via `enabled`.
   */
  const partnerIdRaw = Number(partnerIdParam);
  const partnerId = Number.isFinite(partnerIdRaw) && partnerIdRaw > 0 ? partnerIdRaw : 0;

  const { setPageHeaderSlots } = usePageHeader();
  const confirm = useConfirm();
  const systemAlert = useSystemAlert();

  /* ---------------- Page header actions ---------------- */

  useEffect(() => {
    setPageHeaderSlots({
      actions: [
        {
          key: 'download-template',
          label: 'Download Template',
          startIcon: <Download />,
          variant: 'outlined',
          onClick: () => {
            downloadFile(PARTNER_PRICE_UPLOAD_TEMPLATE, 'partner_price_upload_template');
            systemAlert.success('Template download started.');
          },
        },
      ],
    });
  }, [setPageHeaderSlots, systemAlert]);

  /* ---------------- Queries ---------------- */

  const {
    data: historiesData,
    refetch: refetchHistories,
    isLoading: isLoadingHistories,
  } = usePriceUploadHistories(partnerId, { pageNo });

  const {
    data: totalCount,
    refetch: refetchCount,
    isLoading: isLoadingCount,
  } = usePriceUploadHistoriesCount(partnerId);

  const isTableLoading = Boolean(isLoadingHistories || isLoadingCount);
  const total = totalCount ?? 0;

  /* ---------------- Derived rows ---------------- */

  const rows: PartnerProductPriceBulkUpload[] = useMemo(() => {
    const list = (historiesData?.data ?? []) as PartnerProductPriceBulkUpload[];
    return list.filter((r) => !r.isDeleted);
  }, [historiesData?.data]);

  /* ---------------- Handlers ---------------- */

  const handleChangePage = useCallback((next: number) => {
    setPageNo(next);
  }, []);

  const refetchAll = useCallback(async () => {
    await Promise.all([refetchHistories(), refetchCount()]);
  }, [refetchCount, refetchHistories]);

  const handleConfirmFile = useCallback(
    async (file: File) => {
      if (!partnerId) {
        systemAlert.error('Invalid partnerId.');
        return;
      }

      try {
        const { data, message } = await uploadPriceFile(partnerId, file);

        if (message !== 'OK') {
          systemAlert.error(message || 'Upload failed.');
          return;
        }

        const uploadId = Number(data?.[0]);
        if (!Number.isFinite(uploadId) || uploadId <= 0) {
          systemAlert.error('Upload succeeded but returned an invalid uploadId.');
          return;
        }

        systemAlert.success('File uploaded.');
        await refetchAll();
        navigate(`/partners/${partnerId}/prices/upload/${uploadId}`);
      } catch {
        systemAlert.error('Failed to upload file. Please try again.');
      }
    },
    [navigate, partnerId, refetchAll, systemAlert],
  );

  const handleDeleteUpload = useCallback(
    async (uploadId: number) => {
      if (!partnerId) {
        systemAlert.error('Invalid partnerId.');
        return;
      }

      const ok = await confirm({
        priority: 'warning',
        title: 'Delete this history?',
        description: 'This action cannot be undone.',
        confirmText: 'Delete',
        cancelText: 'Cancel',
        autoFocusConfirm: false,
      });

      if (!ok) return;

      try {
        await deletePriceUploadHistory(partnerId, uploadId);
        systemAlert.success('Deleted.');
        await refetchAll();
      } catch {
        systemAlert.error('Failed to delete history. Please try again.');
      }
    },
    [confirm, partnerId, refetchAll, systemAlert],
  );

  // (# + FileName + Total + Success + Failed + Status + UploadedAt + UpdatedAt + Delete)
  const columnsCount = 9;

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      {/* Upload area */}
      <Box mt={1}>
        <DragDropFile onConfirmFile={handleConfirmFile} />
      </Box>

      {/* Standard toolbar (Total + Page Jump) */}
      <StandardTableToolbar
        page={pageNo}
        pageSize={PAGE_SIZE}
        totalCount={total}
        onChangePage={handleChangePage}
        showTotalCount
        showPageJump
      />

      {/* Table */}
      <Box mt={1}>
        <StandardTableFrame<PartnerProductPriceBulkUpload>
          containerSx={{ borderRadius: 2 }}
          minWidth={1250}
          columnsCount={columnsCount}
          isLoading={isTableLoading}
          loadingVariant="skeleton"
          skeletonRows={6}
          emptyText="No upload history found."
          /**
           * Row status policy
           * - Any rows with failures are marked as warning for quick operator scanning.
           */
          getRowStatus={(row) => ((row.failedCount ?? 0) > 0 ? 'warning' : 'normal')}
          head={
            <TableRow>
              <TableCell width={80} align="right">
                #
              </TableCell>
              <TableCell sx={{ minWidth: 280 }}>File name</TableCell>
              <TableCell width={130} align="right">
                Total
              </TableCell>
              <TableCell width={130} align="right">
                Success
              </TableCell>
              <TableCell width={130} align="right">
                Failed
              </TableCell>
              <TableCell width={160} align="center">
                Status
              </TableCell>
              <TableCell width={220}>Uploaded at</TableCell>
              <TableCell width={220}>Updated at</TableCell>
              <TableCell width={90} align="center">
                Delete
              </TableCell>
            </TableRow>
          }
          rows={rows}
          getRowKey={(row) => String(row.partnerProductPriceBulkUploadId)}
          renderRow={(history, index) => {
            const uploadId = Number(history.partnerProductPriceBulkUploadId);
            const statusChip = getBulkRunStatusMeta(history.status);

            return (
              <>
                {/* Index click -> detail */}
                <TableCell align="right" sx={cellSx.number}>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => navigate(`/partners/${partnerId}/prices/upload/${uploadId}`)}
                    sx={{
                      minWidth: 0,
                      px: 0.5,
                      fontWeight: 800,
                      textDecoration: 'underline',
                      textUnderlineOffset: '3px',
                    }}
                  >
                    {(pageNo - 1) * PAGE_SIZE + index + 1}
                  </Button>
                </TableCell>

                <TableCell sx={{ minWidth: 280 }}>
                  {history.fileUrl ? (
                    <Typography
                      component="a"
                      href={history.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="body2"
                      color="primary"
                      sx={{ wordBreak: 'break-word', textDecoration: 'underline' }}
                    >
                      {history.filename || '-'}
                    </Typography>
                  ) : (
                    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                      {history.filename || '-'}
                    </Typography>
                  )}
                </TableCell>

                <TableCell align="right" sx={cellSx.nowrap}>
                  {formatNumber(history.totalCount)}
                </TableCell>

                <TableCell align="right" sx={cellSx.nowrap}>
                  {formatNumber(history.successCount)}
                </TableCell>

                <TableCell align="right" sx={cellSx.nowrap}>
                  {formatNumber(history.failedCount)}
                </TableCell>

                <TableCell align="center" sx={cellSx.nowrap}>
                  <Chip
                    label={statusChip.label}
                    color={statusChip.chip.color}
                    size="small"
                    variant={statusChip.chip.variant}
                  />
                </TableCell>

                <TableCell sx={cellSx.nowrap}>{formatTimestamp(history.createdAt)}</TableCell>
                <TableCell sx={cellSx.nowrap}>{formatTimestamp(history.updatedAt)}</TableCell>

                <TableCell align="center">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteUpload(uploadId);
                    }}
                    aria-label="Delete upload history"
                  >
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </TableCell>
              </>
            );
          }}
        />
      </Box>

      {/* Bottom pagination */}
      <Box mt={2} display="flex" justifyContent="center">
        <StandardPagination
          page={pageNo}
          pageSize={PAGE_SIZE}
          totalCount={total}
          onChangePage={handleChangePage}
          size="small"
          shape="rounded"
        />
      </Box>
    </Box>
  );
};

export default PartnerPriceUpload;
